import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { catchError, throwError } from 'rxjs';

/**
 * Fragment of the session renewal URL — the only path this interceptor watches.
 */
const REFRESH_ROUTE = '/auth/refresh_token';

/**
 * An unrecoverable session goes back to the login screen instead of leaving the
 * page zeroed out.
 *
 * ## The problem this covers
 *
 * When the refresh fails, `ui-oauth` has **two** paths and they behave
 * differently:
 *
 * - Through `TokenRefreshInterceptor` (refresh triggered by a data request): the
 *   401 calls `logout()`. That works.
 * - Through `setupAutomaticRefresh()` (refresh triggered by the timer, which is
 *   the case for anyone leaving the tab open): the error is **swallowed**
 *   (`.subscribe({ error: () => undefined })`). Nobody logs out, and the data
 *   requests waiting for the new token park in the queue
 *   (`takeUntil(tokenExpires$)`) without ever firing or failing.
 *
 * The result observed in a demo: the dashboard standing there with every value at
 * R$ 0,00, **zero** data requests in DevTools and a repeated 401 on
 * `refresh_token`. Nothing told the user they only had to sign in again — and the
 * "Entrar" button did not help either, because the dead session was still in
 * storage.
 *
 * ## Why the mock's `refresh_expires_in` is not enough
 *
 * That field (added alongside this interceptor) fixes the **expired** refresh
 * token case: with a real date in storage, `isRefreshTokenValid` turns `false` and
 * the package itself calls `logout()`. But expiry is one of several reasons for a
 * 401 — a rotated secret, corrupted storage, a recreated mock database. This
 * interceptor covers all of them: if the renewal was refused, the session is not
 * coming back, and insisting only produces an empty screen.
 *
 * It rethrows the error (`throwError`) instead of swallowing it: callers can still
 * handle it, and the package behaviour does not change — it only gains an exit.
 *
 * @param request outgoing request, inspected for the renewal route
 * @param next next handler in the interceptor chain
 * @returns the handler's stream, with the logout side effect on a refused renewal
 */
export const sessionRecoveryInterceptor: HttpInterceptorFn = (request, next) => {
  if (!request.url.includes(REFRESH_ROUTE)) {
    return next(request);
  }

  const tokens = inject(TokenHandlerService);
  const router = inject(Router);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // 401 and 400 are what the package itself treats as an unrecoverable session
      // (`shouldLogoutAfterRefreshError`); 5xx or a dropped network may be
      // transient and must not tear the session down.
      if (error.status === 401 || error.status === 400) {
        tokens.clearTokensFromStorage();
        router.navigateByUrl('/login');
      }

      return throwError(() => error);
    }),
  );
};
